/**
 * BuilderSidebar — a fixed three-workspace shell (Sections / Design / Settings).
 *
 * UI/UX redesign only: the top-level Sections/Design/Settings navigation used to be a
 * one-shot menu that disappeared the moment you picked one (replaced by a full-screen
 * "Back" header). It's now a PERMANENT nav block pinned to the top of the sidebar — only
 * the workspace content below it changes when you switch tabs or drill into a sub-screen
 * (an individual section's editor, or Settings' Location/Social sub-panels). No editor
 * component, control, setter, schema, or rendering logic changed — only where/how they're
 * mounted and how the transition between them looks.
 *
 *   [Sections] [Design] [Settings]   ← always visible, a 3-column card grid
 *   ─────────────────────────────────
 *   Workspace content for the active tab      ← swaps (fade/slide), scrolls independently
 *     └─ Sections → section list → one section's editor (Back returns to the list)
 *     └─ Settings → settings root → Location details / Social links (Back returns to root)
 */
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Palette, Settings as SettingsIcon, LayoutGrid } from 'lucide-react'
import type { ComponentType, CSSProperties } from 'react'
import { BRAND } from '../../config'
import { DesignPanel } from './DesignPanel'
import { SettingsPanel } from './SettingsPanel'
import { LocationPanel } from './LocationPanel'
import { SocialPanel } from './SocialPanel'
import { SectionsPanel } from './SectionsPanel'
import { SectionEditorPanel } from './SectionEditorPanel'
import { NavbarEditorPanel } from './NavbarEditorPanel'
import { FooterEditorPanel } from './FooterEditorPanel'
import { BannerEditorPanel } from './BannerEditorPanel'
import { AboutEditorPanel } from './AboutEditorPanel'
import { ServicesEditorPanel } from './ServicesEditorPanel'
import { GalleryEditorPanel } from './GalleryEditorPanel'
import { ReviewsEditorPanel } from './ReviewsEditorPanel'
import { FaqEditorPanel } from './FaqEditorPanel'
import { ContactEditorPanel } from './ContactEditorPanel'
import { ScreenHeader } from './controls'
import { useBuilderEditor, SECTION_KIND_LABEL } from './editorContext'

type WorkspaceTab = 'sections' | 'design' | 'settings'
type SettingsSub = 'root' | 'location' | 'social'

/** Soft, pastel accents distinct from the per-section-kind colors in SectionsPanel.tsx, so
 * the two color sets never visually collide even though both are in the same sidebar. */
const TAB_ACCENT: Record<WorkspaceTab, { bg: string; border: string; text: string }> = {
  sections: { bg: 'bg-sky-50', border: 'border-sky-100', text: 'text-sky-600' },
  design: { bg: 'bg-fuchsia-50', border: 'border-fuchsia-100', text: 'text-fuchsia-600' },
  settings: { bg: 'bg-zinc-50', border: 'border-zinc-200', text: 'text-zinc-600' },
}

const TAB_NAV: readonly { tab: WorkspaceTab; label: string; description: string; icon: ComponentType<{ className?: string; style?: CSSProperties }> }[] = [
  { tab: 'sections', label: 'Sections', description: 'Layout, order', icon: LayoutGrid },
  { tab: 'design', label: 'Design', description: 'Colors, type', icon: Palette },
  { tab: 'settings', label: 'Settings', description: 'Site, social', icon: SettingsIcon },
]

/** `BRAND.red` as a soft tint/border — same brand-color language SectionsPanel.tsx uses for
 * its own active row, kept local here since this is the only other place that needs it. */
function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}
const ACTIVE_TINT_BG = hexToRgba(BRAND.red, 0.07)
const ACTIVE_TINT_BORDER = hexToRgba(BRAND.red, 0.22)

/** One square workspace-selector card — icon on top, title + a short muted subtitle below. */
function WorkspaceNavCard({
  label,
  description,
  icon: Icon,
  accent,
  active,
  onClick,
}: {
  label: string
  description: string
  icon: ComponentType<{ className?: string; style?: CSSProperties }>
  accent: { bg: string; border: string; text: string }
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group flex aspect-square flex-col items-center justify-center gap-1.5 rounded-2xl border p-2 text-center transition-all duration-200 ${
        active ? 'shadow-sm' : 'border-gray-100 bg-white hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md'
      }`}
      style={active ? { backgroundColor: ACTIVE_TINT_BG, borderColor: ACTIVE_TINT_BORDER } : undefined}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all duration-200 ${
          active ? '' : `${accent.bg} ${accent.border} group-hover:shadow-sm`
        }`}
        style={active ? { backgroundColor: ACTIVE_TINT_BG, borderColor: ACTIVE_TINT_BORDER } : undefined}
      >
        <Icon
          className={`h-5 w-5 transition-transform duration-200 group-hover:scale-105 ${active ? '' : accent.text}`}
          style={active ? { color: BRAND.red } : undefined}
        />
      </span>
      <span
        className={`text-[13px] leading-tight transition-colors duration-200 ${active ? 'font-semibold' : 'font-medium text-gray-800'}`}
        style={active ? { color: BRAND.red } : undefined}
      >
        {label}
      </span>
      <span className="line-clamp-2 text-[10.5px] leading-tight text-gray-400">{description}</span>
    </button>
  )
}

/** Maps a section kind to its dedicated editor — identical panels/props as before, just relocated. */
function SectionEditor({ sectionId, kind }: { sectionId: string; kind: string }) {
  switch (kind) {
    case 'navbar':
      return <NavbarEditorPanel sectionId={sectionId} />
    case 'footer':
      return <FooterEditorPanel sectionId={sectionId} />
    case 'hero':
      return <BannerEditorPanel sectionId={sectionId} />
    case 'about':
      return <AboutEditorPanel sectionId={sectionId} />
    case 'services':
      return <ServicesEditorPanel sectionId={sectionId} />
    case 'gallery':
      return <GalleryEditorPanel sectionId={sectionId} />
    case 'reviews':
      return <ReviewsEditorPanel sectionId={sectionId} />
    case 'faq':
      return <FaqEditorPanel sectionId={sectionId} />
    case 'contact':
      return <ContactEditorPanel sectionId={sectionId} />
    default:
      return <SectionEditorPanel sectionId={sectionId} />
  }
}

export function BuilderSidebar() {
  const { schema, previewActiveSectionId, requestScrollToSection } = useBuilderEditor()
  const [tab, setTab] = useState<WorkspaceTab>('sections')
  const [sectionId, setSectionId] = useState<string | null>(null)
  const [settingsSub, setSettingsSub] = useState<SettingsSub>('root')

  const activeKind = schema.pages[0]?.sections.find((s) => s.id === sectionId)?.kind

  // Switching workspaces always lands on that workspace's own root — a fresh, predictable
  // starting point rather than resuming whatever sub-screen was open last time.
  const selectTab = (next: WorkspaceTab) => {
    setTab(next)
    setSectionId(null)
    setSettingsSub('root')
  }

  // Section Navigation UX: opening a section also scrolls Live Preview to it. While no
  // section editor is open, the list instead highlights whatever the preview reports as
  // currently visible (kept in sync by its own IntersectionObserver — see LivePreviewPage),
  // so the sidebar tracks manual scrolling too, not just clicks.
  const openSection = (id: string) => {
    setSectionId(id)
    requestScrollToSection(id)
  }
  const closeSection = () => setSectionId(null)
  const activeSectionId = sectionId ?? previewActiveSectionId

  const subHeader =
    tab === 'sections' && sectionId && activeKind
      ? { title: SECTION_KIND_LABEL[activeKind], onBack: closeSection }
      : tab === 'settings' && settingsSub !== 'root'
        ? { title: settingsSub === 'location' ? 'Location details' : 'Social links', onBack: () => setSettingsSub('root') }
        : null

  // Distinct per screen so AnimatePresence treats each as a new "page" to fade/slide between.
  const contentKey = `${tab}:${sectionId ?? ''}:${settingsSub}`

  return (
    <div className="flex h-full flex-col">
      {/* Fixed top workspace nav — always visible, never replaced by whatever is selected.
          A compact 3-column grid of square cards rather than stacked full-width rows, so it
          reads as a workspace selector (Framer/Webflow-style) and takes up less vertical
          space than three full rows would. */}
      <nav aria-label="Workspace" className="grid grid-cols-1 gap-2 px-2 pb-2 pt-3 sm:grid-cols-3">
        {TAB_NAV.map((item) => (
          <WorkspaceNavCard
            key={item.tab}
            label={item.label}
            description={item.description}
            icon={item.icon}
            accent={TAB_ACCENT[item.tab]}
            active={tab === item.tab}
            onClick={() => selectTab(item.tab)}
          />
        ))}
      </nav>

      {/* Workspace content — visually separated from the fixed nav by spacing (not a heavy
          border), scrolls independently, and fades/slides between screens. */}
      <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100">
        {subHeader ? <ScreenHeader title={subHeader.title} onBack={subHeader.onBack} /> : null}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={contentKey}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            {tab === 'sections' ? (
              sectionId && activeKind ? (
                <SectionEditor sectionId={sectionId} kind={activeKind} />
              ) : (
                <SectionsPanel onOpen={openSection} activeSectionId={activeSectionId} />
              )
            ) : tab === 'design' ? (
              <DesignPanel />
            ) : settingsSub === 'location' ? (
              <LocationPanel />
            ) : settingsSub === 'social' ? (
              <SocialPanel />
            ) : (
              <SettingsPanel onOpenLocation={() => setSettingsSub('location')} onOpenSocial={() => setSettingsSub('social')} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
